import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, LogOut } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { adminApi, api, errMsg, ADMIN_TOKEN_KEY } from "@/lib/api";
import { Logo } from "@/components/Header";
import { AdminStats } from "@/pages/admin/AdminStats";
import { AdminGame } from "@/pages/admin/AdminGame";
import { AdminUsers } from "@/pages/admin/AdminUsers";
import { AdminTx } from "@/pages/admin/AdminTx";
import { AdminBonuses } from "@/pages/admin/AdminBonuses";
import { AdminPayment } from "@/pages/admin/AdminPayment";

const AdminLogin = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { username: email, password });
      if (data.user.role !== "admin") throw new Error("Bu hesap yönetici değil");
      localStorage.setItem(ADMIN_TOKEN_KEY, data.token);
      onLogin();
    } catch (err) {
      toast.error(errMsg(err));
    }
    setLoading(false);
  };
  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <form onSubmit={submit} className="x-panel p-7 w-full max-w-sm space-y-4 x-rise" data-testid="admin-login-form">
        <Logo />
        <div className="text-xl font-extrabold">Yönetim Paneli</div>
        <div>
          <label className="x-label">E-posta</label>
          <input data-testid="admin-email-input" className="x-input" value={email} onChange={(e) => setEmail(e.target.value)} required />
        </div>
        <div>
          <label className="x-label">Şifre</label>
          <input data-testid="admin-password-input" type="password" className="x-input" value={password} onChange={(e) => setPassword(e.target.value)} required />
        </div>
        <button data-testid="admin-login-btn" disabled={loading} className="x-btn x-btn-primary w-full">
          {loading && <Loader2 size={18} className="animate-spin" />} Giriş Yap
        </button>
      </form>
    </div>
  );
};

const TABS = [
  ["stats", "Genel", AdminStats],
  ["game", "Oyun Ayarları", AdminGame],
  ["users", "Kullanıcılar", AdminUsers],
  ["deposits", "Yatırımlar", (p) => <AdminTx kind="deposits" {...p} />],
  ["withdrawals", "Çekimler", (p) => <AdminTx kind="withdrawals" {...p} />],
  ["bonuses", "Bonuslar", AdminBonuses],
  ["payment", "Site, Ödeme & Ortaklık", AdminPayment],
];

export default function Admin() {
  const [ok, setOk] = useState(null);
  const check = () =>
    adminApi.get("/auth/me").then((r) => setOk(r.data.role === "admin")).catch(() => setOk(false));
  useEffect(() => {
    if (localStorage.getItem(ADMIN_TOKEN_KEY)) check();
    else setOk(false);
  }, []);

  if (ok === null) return null;
  if (!ok) return <AdminLogin onLogin={check} />;

  const logout = () => {
    localStorage.removeItem(ADMIN_TOKEN_KEY);
    setOk(false);
  };

  return (
    <div className="min-h-screen" data-testid="admin-panel">
      <header className="h-16 border-b border-black/30 bg-[var(--x-bg)] px-4 flex items-center justify-between sticky top-0 z-30">
        <div className="flex items-center gap-3"><Logo /><span className="x-chip bg-[var(--x-blue)]">ADMIN</span></div>
        <button data-testid="admin-logout-btn" onClick={logout} className="x-btn x-btn-secondary px-3 py-2"><LogOut size={16} /> Çıkış</button>
      </header>
      <div className="max-w-7xl mx-auto p-4">
        <Tabs defaultValue="stats">
          <TabsList className="bg-[var(--x-bg-deep)] h-auto flex-wrap justify-start p-1.5 gap-1">
            {TABS.map(([k, l]) => (
              <TabsTrigger key={k} value={k} data-testid={`admin-tab-${k}`} className="font-bold data-[state=active]:bg-[var(--x-panel-2)] data-[state=active]:text-white">
                {l}
              </TabsTrigger>
            ))}
          </TabsList>
          {TABS.map(([k, , C]) => (
            <TabsContent key={k} value={k} className="mt-4">
              <C />
            </TabsContent>
          ))}
        </Tabs>
      </div>
    </div>
  );
}
